import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useMemo,
  useRef,
} from "react";
import { GetApiMethod } from "../../../Utils/ApiService";
import { CategoryEndpoint } from "../../../Api/Api";

// Category type
export interface Category {
  id: string;
  name: string;
  status: boolean;
  file_key?: string;
  photos?: string[];
  createdAt?: string;
  updatedAt?: string;
}

// Subcategory type
export interface SubCategory {
  id: string;
  name: string;
  categoryId: string;
  status: boolean;
  createdAt?: string;
  updatedAt?: string;
}

// Service type
export interface Service {
  id: string;
  name: string;
  categoryId: string;
  subcategoryId?: string;
  type?: string; // e.g., "Addons", "Main Service"
  status: boolean;
  price?: number;
  createdAt?: string;
  updatedAt?: string;
}

// Context type
interface CategoryContextType {
  allCategories: Category[];
  setAllCategories: React.Dispatch<React.SetStateAction<Category[]>>;
  allSubcategories: SubCategory[];
  setAllSubcategories: React.Dispatch<React.SetStateAction<SubCategory[]>>;
  allServices: Service[];
  setAllServices: React.Dispatch<React.SetStateAction<Service[]>>;
  selectedCategoryId: string | null;
  setSelectedCategoryId: (id: string | null) => void;
  loading: boolean;
  fetchCategories: () => Promise<void>;
  fetchSubcategories: () => Promise<void>;
  fetchServices: () => Promise<void>;
}

const CategoryContext = createContext<CategoryContextType | null>(null);

export const CategoryProvider = ({ children }: { children: ReactNode }) => {
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<SubCategory[]>([]);
  const [allServices, setAllServices] = useState<Service[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const hasFetched = useRef(false);

  // Fetch categories
  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await GetApiMethod(
        `${CategoryEndpoint.CATEGORY_LIST_URL}?_=${new Date().getTime()}`
      );
      const records = response?.data?.data?.records || [];
      setAllCategories(records);
    } catch (error) {
      console.error("Error fetching categories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch subcategories
  const fetchSubcategories = async () => {
    try {
      setLoading(true);
      const response = await GetApiMethod(`sub-category?_=${new Date().getTime()}`);
      const records = response?.data?.data?.records || [];
      setAllSubcategories(records);
    } catch (error) {
      console.error("Error fetching subcategories:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch services
  const fetchServices = async () => {
    try {
      setLoading(true);
      const response = await GetApiMethod(`service/list?type=Services&_=${new Date().getTime()}`);
      const records = response?.data?.data?.records || [];
      setAllServices(records);
    } catch (error) {
      console.error("Error fetching services:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all on mount — guarded against double execution in StrictMode
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    fetchCategories();
    fetchSubcategories();
    fetchServices();
  }, []);

  // useMemo to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      allCategories,
      setAllCategories,
      allSubcategories,
      setAllSubcategories,
      allServices,
      setAllServices,
      selectedCategoryId,
      setSelectedCategoryId,
      loading,
      fetchCategories,
      fetchSubcategories,
      fetchServices,
    }),
    [
      allCategories,
      allSubcategories,
      allServices,
      selectedCategoryId,
      loading,
    ]
  );

  return (
    <CategoryContext.Provider value={contextValue}>
      {children}
    </CategoryContext.Provider>
  );
};

// Custom hook for consuming the context
export const useCategory = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error("useCategory must be used within a CategoryProvider");
  }
  return context;
};

// Custom hook to get subcategories filtered by category
export const useSubcategories = (categoryId?: string) => {
  const { allSubcategories } = useCategory();
  if (!categoryId) return [];
  return allSubcategories.filter(
    (sub) => sub.categoryId === categoryId && sub.status
  );
};

// Custom hook to get all active services
export const useServices = () => {
  const { allServices } = useCategory();
  return allServices.filter((service) => service.status);
};
